import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContentHighPriority, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Send, Loader2 } from 'lucide-react';
import { useSolicitarFatura } from '@/hooks/useSolicitarFatura';
import { formatarTelefone } from '@/utils/formatters';
import { normalizarTelefoneBR, validarTelefoneBR } from '@/lib/formatters';
import { useQueryClient } from '@tanstack/react-query';

interface SolicitarFaturaButtonProps {
  clienteId?: string;
  cnpj: string;
  linhaPlanoInicial?: string;
  // NOVO: pré-preencher destinatário (ex: telefone do gestor na Qualidade)
  linhaSolicitanteInicial?: string;
}

export function SolicitarFaturaButton({
  clienteId,
  cnpj,
  linhaPlanoInicial,
  linhaSolicitanteInicial,
}: SolicitarFaturaButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [linhaPlanoRaw, setLinhaPlanoRaw] = useState('');
  const [linhaSolicitanteRaw, setLinhaSolicitanteRaw] = useState('');
  const queryClient = useQueryClient();

  const { solicitar, temAndamento } = useSolicitarFatura(clienteId);

  // Normalizar telefones
  const linhaPlano = normalizarTelefoneBR(linhaPlanoRaw);
  const linhaSolicitante = normalizarTelefoneBR(linhaSolicitanteRaw);
  
  // Validar
  const validacaoPlano = validarTelefoneBR(linhaPlano);
  const validacaoSolicitante = validarTelefoneBR(linhaSolicitante);
  
  // Para o botão, exigimos exatamente 11 dígitos (celular)
  const linhaPlanoValida = linhaPlano.length === 11;
  const linhaSolicitanteValida = linhaSolicitante.length === 11;
  const linhaSolicitanteAceita = linhaSolicitante.length === 0 || linhaSolicitanteValida;

  // Pré-preencher quando abrir modal
  useEffect(() => {
    if (isOpen && linhaPlanoInicial && !linhaPlanoRaw) {
      setLinhaPlanoRaw(linhaPlanoInicial);
    }
    if (isOpen && linhaSolicitanteInicial && !linhaSolicitanteRaw) {
      setLinhaSolicitanteRaw(linhaSolicitanteInicial);
    }
  }, [isOpen, linhaPlanoInicial, linhaSolicitanteInicial, linhaPlanoRaw, linhaSolicitanteRaw]);

  const handleSubmit = async () => {
    // Enviar números já normalizados (11 dígitos limpos)
    await solicitar.mutateAsync({ 
      cnpj, 
      linha_plano: linhaPlano, 
      linha_solicitante: linhaSolicitante 
    });
    queryClient.invalidateQueries({ queryKey: ['todas-faturas'] });
    queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
    queryClient.invalidateQueries({ queryKey: ['leads-carteira'] });
    setIsOpen(false);
    setLinhaPlanoRaw('');
    setLinhaSolicitanteRaw('');
  };

  return (
    <>
      <Button 
        onClick={() => setIsOpen(true)} 
        size="lg"
        className="h-9 rounded-lg border-0 bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-4 text-[12.5px] font-bold !text-white shadow-[0_2px_6px_rgba(91,10,13,0.30)] transition-all hover:brightness-110 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]"
      >
        {temAndamento ? (
          <>
            <Send className="h-4 w-4 mr-2" />
            Pedir com prioridade
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" />
            Solicitar Fatura
          </>
        )}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContentHighPriority>
          <DialogHeader>
            <DialogTitle>Solicitar Fatura</DialogTitle>
            <DialogDescription>
              O pedido manual tem prioridade 0 e pode ser feito mesmo com outro pedido em andamento. O WhatsApp de destino é opcional.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>CNPJ</Label>
              <Input value={cnpj} disabled />
            </div>

            <div>
              <Label>Linha do Plano *</Label>
              <Input
                placeholder="(11) 98765-4321"
                value={linhaPlanoRaw}
                onChange={(e) => setLinhaPlanoRaw(e.target.value)}
              />
              {linhaPlanoValida ? (
                <p className="text-xs text-success mt-1">
                  {formatarTelefone(linhaPlano)}
                </p>
              ) : linhaPlano.length > 0 && !validacaoPlano.valido ? (
                <p className="text-xs text-primary mt-1">
                  {validacaoPlano.erro}
                </p>
              ) : null}
              {linhaPlanoInicial && (
                <p className="text-xs text-muted-foreground mt-1">
                  Linha salva para este CNPJ
                </p>
              )}
            </div>

            <div>
              <Label>WhatsApp de destino (opcional)</Label>
              <Input
                placeholder="(11) 91234-5678"
                value={linhaSolicitanteRaw}
                onChange={(e) => setLinhaSolicitanteRaw(e.target.value)}
              />
              {linhaSolicitanteValida ? (
                <p className="text-xs text-success mt-1">
                  {formatarTelefone(linhaSolicitante)}
                </p>
              ) : linhaSolicitante.length > 0 && !validacaoSolicitante.valido ? (
                <p className="text-xs text-primary mt-1">
                  {validacaoSolicitante.erro}
                </p>
              ) : linhaSolicitante.length === 10 ? (
                <p className="text-xs text-primary mt-1">
                  WhatsApp precisa de 11 dígitos (com 9)
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground mt-1">
                Deixe vazio para salvar a fatura somente no CRM.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!linhaPlanoValida || !linhaSolicitanteAceita || solicitar.isPending}
            >
              {solicitar.isPending ? 'Enviando...' : 'Solicitar'}
            </Button>
          </DialogFooter>
        </DialogContentHighPriority>
      </Dialog>
    </>
  );
}
